import type { Facing } from "../movement/step.ts";
import { unpackOffsetX, unpackOffsetY } from "../movement/true-point.ts";
import { awayOfStatus, badgeOfStatus, infamyOfStatus } from "./away.ts";
import { HEADER_BYTES, MALFORMED, OK, checkHeader, viewOf, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { NO_HANDLE } from "./limits.ts";
import { NO_NAME, readName } from "./names.ts";
import { CROSSED_FLAG, Component, KEYFRAME_FLAG, MAX_HEIGHT, RUNNING_BIT, SNAPSHOT_HEADER_BYTES, wellFormed, type DecodedEntity, type SnapshotRecord } from "./snapshot.ts";

/**
 * The decoder of the `snapshot` frame (PROTOCOL.md 5.2 and 5.3), on the client's side of the layout `snapshot.ts`
 * encodes. It never throws.
 */

/**
 * Decodes a snapshot frame into a record. Never throws: any length, version, type or count fault, a spawn
 * without every component but the Name, or a name longer than its bound or not UTF-8, closes with the
 * malformed code. A record without the Name reads `NO_NAME`; the view decides what name the entity keeps.
 *
 * @param frame The whole frame.
 * @param out Record to fill; its contents are unspecified unless the verdict is `ok`.
 * @returns The verdict.
 */
export function decodeSnapshot(frame: Uint8Array, out: SnapshotRecord): DecodeVerdict {
  return decodeSnapshotFrame(frame, out, MessageType.Snapshot);
}

/**
 * Decodes the snapshot layout under the message type it is carried as: the `snapshot` itself, and `edgeView`
 * (PROTOCOL.md 5.24). Never throws.
 *
 * @param frame The whole frame.
 * @param out Record to fill; its contents are unspecified unless the verdict is `ok`.
 * @param type The message id the header must carry.
 * @returns The verdict.
 */
export function decodeSnapshotFrame(frame: Uint8Array, out: SnapshotRecord, type: number): DecodeVerdict {
  if (frame.length < SNAPSHOT_HEADER_BYTES) {
    return MALFORMED;
  }
  const header = checkHeader(frame, type, frame.length);
  if (header.kind !== "ok") {
    return header;
  }
  const view = viewOf(frame);
  const entityCount = view.getUint16(HEADER_BYTES + 13, true);
  const removedCount = view.getUint16(HEADER_BYTES + 15, true);
  const entities: DecodedEntity[] = [];
  let offset = SNAPSHOT_HEADER_BYTES;
  for (let i = 0; i < entityCount; i += 1) {
    if (offset + 4 > frame.length) {
      return MALFORMED;
    }
    const handle = view.getUint16(offset, true);
    const flags = view.getUint16(offset + 2, true);
    offset += 4;
    if (!wellFormed(flags)) {
      return MALFORMED;
    }
    let x = 0;
    let y = 0;
    let facing = 0;
    let anim = 0;
    let health = 0;
    let height = 0;
    let running = false;
    let pvp = 0;
    let infamy = 0;
    let away = 0;
    let badge = 0;
    let buffs: number[] = [];
    let kind = 0;
    let appearance = 0;
    let name = NO_NAME;
    let level = 0;
    let offsetX = 0;
    let offsetY = 0;
    let companion = 0;
    let cloakCut = 0;
    let cloakPalette = 0;
    if (flags & Component.Position) {
      if (offset + 4 > frame.length) return MALFORMED;
      x = view.getUint16(offset, true);
      y = view.getUint16(offset + 2, true);
      offset += 4;
    }
    if (flags & Component.FacingAnim) {
      if (offset + 1 > frame.length) return MALFORMED;
      const byte = view.getUint8(offset);
      facing = byte & 0b11;
      anim = byte >> 2;
      offset += 1;
    }
    if (flags & Component.Health) {
      if (offset + 1 > frame.length) return MALFORMED;
      health = view.getUint8(offset);
      offset += 1;
    }
    if (flags & Component.Height) {
      if (offset + 1 > frame.length) return MALFORMED;
      const byte = view.getUint8(offset);
      height = byte & ~RUNNING_BIT;
      if (height > MAX_HEIGHT) return MALFORMED;
      running = (byte & RUNNING_BIT) !== 0;
      offset += 1;
    }
    if (flags & Component.Pvp) {
      if (offset + 1 > frame.length) return MALFORMED;
      pvp = view.getUint8(offset);
      offset += 1;
    }
    if (flags & Component.Infamy) {
      if (offset + 1 > frame.length) return MALFORMED;
      const status = view.getUint8(offset);
      infamy = infamyOfStatus(status);
      away = awayOfStatus(status);
      badge = badgeOfStatus(status);
      offset += 1;
    }
    if (flags & Component.Buffs) {
      if (offset + 1 > frame.length) return MALFORMED;
      const buffCount = view.getUint8(offset);
      offset += 1;
      if (offset + 2 * buffCount > frame.length) return MALFORMED;
      buffs = new Array<number>(buffCount);
      for (let b = 0; b < buffCount; b += 1) {
        buffs[b] = view.getUint16(offset, true);
        offset += 2;
      }
    }
    if (flags & Component.Name) {
      const read = readName(frame, offset);
      if (read === null) return MALFORMED;
      name = read.name;
      offset = read.end;
    }
    if (flags & Component.Level) {
      if (offset + 1 > frame.length) return MALFORMED;
      level = view.getUint8(offset);
      offset += 1;
    }
    if (flags & Component.Offsets) {
      if (offset + 1 > frame.length) return MALFORMED;
      const packed = view.getUint8(offset);
      offsetX = unpackOffsetX(packed);
      offsetY = unpackOffsetY(packed);
      offset += 1;
    }
    if (flags & Component.Companion) {
      if (offset + 2 > frame.length) return MALFORMED;
      const packed = view.getUint8(offset);
      companion = packed & 0x0f;
      cloakCut = packed >> 4;
      cloakPalette = view.getUint8(offset + 1);
      offset += 2;
    }
    if (flags & Component.Spawn) {
      if (offset + 3 > frame.length) return MALFORMED;
      kind = view.getUint8(offset);
      appearance = view.getUint16(offset + 1, true);
      offset += 3;
    }
    entities.push({ handle, flags, state: { handle, x, y, facing: facing as Facing, anim, health, height, running, pvp, infamy, away, badge, buffs, kind, appearance, level, offsetX, offsetY, companion, cloakCut, cloakPalette, name } });
  }
  const flagsByte = view.getUint8(HEADER_BYTES + 12);
  if ((flagsByte & ~(KEYFRAME_FLAG | CROSSED_FLAG)) !== 0 || offset + 2 * removedCount > frame.length) {
    return MALFORMED;
  }
  const removed: number[] = new Array<number>(removedCount);
  for (let i = 0; i < removedCount; i += 1) {
    removed[i] = view.getUint16(offset, true);
    if (removed[i] === NO_HANDLE) {
      return MALFORMED;
    }
    offset += 2;
  }
  let crossed: number[] = [];
  if ((flagsByte & CROSSED_FLAG) !== 0) {
    if (offset + 2 > frame.length) {
      return MALFORMED;
    }
    const crossedCount = view.getUint16(offset, true);
    offset += 2;
    if (crossedCount === 0 || offset + 2 * crossedCount !== frame.length) {
      return MALFORMED;
    }
    crossed = new Array<number>(crossedCount);
    for (let i = 0; i < crossedCount; i += 1) {
      const handle = view.getUint16(offset, true);
      if (handle === NO_HANDLE || removed.includes(handle)) {
        return MALFORMED;
      }
      crossed[i] = handle;
      offset += 2;
    }
  } else if (offset !== frame.length) {
    return MALFORMED;
  }
  out.tick = view.getUint32(HEADER_BYTES, true);
  out.baseTick = view.getUint32(HEADER_BYTES + 4, true);
  out.ackSeq = view.getUint32(HEADER_BYTES + 8, true);
  out.keyframe = (flagsByte & KEYFRAME_FLAG) !== 0;
  out.entities = entities;
  out.removed = removed;
  out.crossed = crossed;
  return OK;
}
